import { useEffect, useState, type FC } from 'react';
import { useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Edit, Delete, AddCircle } from '@mui/icons-material';
import FETCH_DEVICE_PROFILE, { Device } from '@/graphql/querys/fetchDeviceProfile';
import { DevicesOrderBy, HardwareOptionInput } from '@/__generated__/graphql';
import { TableLoading } from '@/components';
import ProgressIcon from '@features/deviceConfiguration/components/ProgressIcon';
import SheetIcon from '@features/deviceConfiguration/components/SheetIcon';
import { columns } from '@features/deviceConfiguration/data/tableColumns';
import { getSectionInputs } from '@features/deviceConfiguration/data/sectionInputs';
import { get } from 'lodash';
import StandardListPagination from '@features/standardDesign/components/listView/StandardListPagination';
import { Trans, msg } from '@lingui/macro';
import { useLingui } from '@lingui/react';

interface Props {
  type: string | null | undefined;
  searchDeviceInput: string | null | undefined;
  searchProjectInput: string | null | undefined;
  organizationId: number | null | undefined;
  page: number;
  rowsPerPage: number;
  orderBy: DevicesOrderBy;
  hardwareOptions: HardwareOptionInput[] | null;
  hardwareSerialNumbers: string[] | null;
  hardwareImageVersions: string[] | null;
  handlePageChange: (page: number) => void;
  handleRowsPerPageChange: (rows: number) => void;
  handleOrderByChange: (order: DevicesOrderBy) => void;
  handleCreateConfig: (device: Device) => void;
  handleDeleteConfig: (device: Device) => void;
  handleViewConfigDrawer: (device: Device) => void;
}

const DeviceConfigurationTable: FC<Props> = ({
  type,
  searchDeviceInput,
  searchProjectInput,
  organizationId,
  page,
  rowsPerPage,
  orderBy,
  hardwareOptions,
  hardwareSerialNumbers,
  hardwareImageVersions,
  handlePageChange,
  handleRowsPerPageChange,
  handleOrderByChange,
  handleCreateConfig,
  handleDeleteConfig,
  handleViewConfigDrawer,
}) => {
  const { _ } = useLingui();
  const { data, loading } = useQuery(FETCH_DEVICE_PROFILE, {
    variables: {
      input: {
        orderBy: orderBy,
        types: type ? [type] : null,
        organizations: organizationId ? [organizationId] : null,
        search: searchDeviceInput || null,
        project: searchProjectInput,
        hardwareOptions: hardwareOptions,
        hardwareSerialNumbers,
        hardwareImageVersions,
      },
      pagination: {
        limit: rowsPerPage,
        offset: (page - 1) * rowsPerPage,
      },
    },
    notifyOnNetworkStatusChange: true,
  });

  const devices = data?.devices ?? [];
  const totalCount = data?.devicesPageInfo?.totalCount ?? 0;

  const theme = useTheme();
  const mobileDevice = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    handlePageChange(1);
  }, [searchDeviceInput, searchProjectInput, type, organizationId]);

  const handleSortingChange = (accessor: string | undefined) => {
    if (!accessor) return;
    handleOrderByChange(
      `${accessor.toUpperCase()}_${orderBy.split('_')[1] === 'ASC' ? 'DESC' : 'ASC'
      }` as DevicesOrderBy,
    );
  };

  const sectionInputs = getSectionInputs();

  if (mobileDevice) {
    return (
      <>
        {loading && loading ? (
          <Box mt={2}>
            <TableLoading />
          </Box>
        ) : (
          <Box>
            <StandardListPagination
              totalItems={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(page) => handlePageChange(page)}
              onRowsPerPageChange={(rowsPerPage) => {
                handleRowsPerPageChange(rowsPerPage);
              }}
              my={2}
            />
            <Box sx={{ width: '100%', mb: 2 }}>
              <Grid container spacing={2}>
                {devices.map((row) => (
                  <Grid key={row.id} item xs={12}>
                    <Paper
                      variant="outlined"
                      sx={{
                        borderRadius: '10px',
                      }}
                    >
                      <Grid container>
                        <Grid
                          item
                          xs={11}
                          sx={{
                            padding: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              color: '#FFFFFF',
                              fontWeight: 'bold',
                            }}
                          >
                            ID: {row.id}
                          </Typography>
                        </Grid>
                        <Grid
                          item
                          xs={1}
                          sx={{
                            paddingRight: 1,
                            display: row.hardwareConfiguration !== null ? 'flex' : 'none',
                            justifyContent: 'center',
                          }}
                        >
                          <Button onClick={() => handleViewConfigDrawer(row)}>
                            <SheetIcon color="#D4D8D9" />
                          </Button>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid
                            sx={{
                              backgroundColor: '#2a3542',
                            }}
                            container
                          >
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                  fontFamily: 'Sora',
                                }}
                              >
                                <Trans>Proyecto</Trans>
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                }}
                              >
                                <Trans>Dispositivo</Trans>
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                }}
                              >
                                <Trans>Organización</Trans>
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid container>
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'regular',
                                  fontSize: '12px',
                                }}
                              >
                                {row.profile.project}
                              </Typography>
                            </Grid>
                            <Divider orientation="vertical" flexItem sx={{ mr: '-1px' }} />
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'regular',
                                  fontSize: '12px',
                                }}
                              >
                                {row.profile?.name}
                              </Typography>
                            </Grid>
                            <Divider orientation="vertical" flexItem sx={{ mr: '-1px' }} />
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'regular',
                                  fontSize: '12px',
                                }}
                              >
                                {row.profile?.organization?.name}
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid item xs={12}>
                          <Grid
                            sx={{
                              backgroundColor: '#2a3542',
                            }}
                            container
                          >
                            <Grid
                              item
                              xs={8}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                }}
                              >
                                <Trans>Progreso</Trans>
                              </Typography>
                            </Grid>
                            <Grid
                              item
                              xs={4}
                              sx={{
                                padding: 1,
                              }}
                            >
                              <Typography
                                sx={{
                                  color: '#D4D8D9',
                                  fontWeight: 'bold',
                                  fontSize: '12px',
                                  fontFamily: 'Sora',
                                }}
                              >
                                <Trans>Acciones</Trans>
                              </Typography>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid
                          sx={{
                            padding: 1,
                          }}
                          xs={8}
                          item
                        >
                          {Object.keys(sectionInputs)
                            .filter(
                              (section) =>
                                row?.profile?.source !== 'integration' || section === 'general',
                            )
                            .map((section) => (
                              <ProgressIcon
                                key={row.id + section}
                                deviceId={row.id}
                                section={section}
                                isMobile={mobileDevice}
                              />
                            ))}
                        </Grid>
                        <Grid
                          sx={{
                            padding: 1,
                            justifyContent: 'center',
                          }}
                          xs={4}
                          item
                        >
                          {row.hardwareConfiguration !== null ? (
                            <Grid container>
                              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Edit
                                  onClick={() => handleCreateConfig(row)}
                                  sx={{ color: '#D4D8D9', fontSize: '20px' }}
                                />
                              </Grid>
                              <Grid item xs={6} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <Delete
                                  onClick={() => handleDeleteConfig(row)}
                                  sx={{ color: '#D4D8D9', fontSize: '20px' }}
                                />
                              </Grid>
                            </Grid>
                          ) : (
                            <Grid container>
                              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                                <AddCircle
                                  onClick={() => handleCreateConfig(row)}
                                  sx={{ color: '#D4D8D9', fontSize: '20px' }}
                                />
                              </Grid>
                            </Grid>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </Box>

            <StandardListPagination
              totalItems={totalCount}
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={(page) => handlePageChange(page)}
              onRowsPerPageChange={(rowsPerPage) => {
                handleRowsPerPageChange(rowsPerPage);
              }}
              my={2}
            />
          </Box>
        )}
      </>
    );
  }

  return (
    <>
      {loading ? (
        <Box mt={2}>
          <TableLoading />
        </Box>
      ) : (
        <Box>
          <StandardListPagination
            totalItems={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(page) => handlePageChange(page)}
            onRowsPerPageChange={(rowsPerPage) => {
              handleRowsPerPageChange(rowsPerPage);
            }}
            my={2}
          />
          <Paper sx={{ width: '100%', mb: 2 }}>
            <TableContainer>
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow
                    sx={{
                      backgroundColor: '#2a3542',
                    }}
                  >
                    {columns().map(({ label, sortable, db_accessor }) => {
                      return (
                        <TableCell
                          key={'header-' + label}
                          onClick={sortable ? () => handleSortingChange(db_accessor) : undefined}
                        >
                          <TableSortLabel
                            active={orderBy.split('_')[0].toLowerCase() === db_accessor}
                            direction={orderBy.split('_')[1].toLowerCase() as 'desc' | 'asc'}
                            hideSortIcon={!sortable}
                          >
                            <Typography>{label}</Typography>
                          </TableSortLabel>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {devices.map((row) => (
                    <TableRow
                      hover
                      key={get(row, 'id', 0)}
                      sx={{
                        cursor: 'pointer',
                      }}
                    >
                      <TableCell component="th" scope="row">
                        {row.id}
                      </TableCell>
                      <TableCell onClick={() => handleViewConfigDrawer(row)}>
                        {row.profile.name}
                      </TableCell>
                      <TableCell>{row.profile.type}</TableCell>
                      <TableCell>{row.profile.organization?.name}</TableCell>
                      <TableCell>{row.profile.project}</TableCell>
                      <TableCell>
                        {Object.keys(sectionInputs)
                          .filter(
                            (section) =>
                              row?.profile?.source !== 'integration' || section === 'general',
                          )
                          .map((section) => (
                            <ProgressIcon
                              key={row.id + section}
                              deviceId={row.id}
                              section={section}
                              isMobile={mobileDevice}
                            />
                          ))}
                      </TableCell>
                      <TableCell>
                        <Box>
                          {row?.hardwareConfiguration !== null ? (
                            <>
                              <Tooltip title={_(msg`Editar`)}>
                                <Button onClick={() => handleCreateConfig(row)}>
                                  <Edit sx={{ color: '#D4D8D9' }} />
                                </Button>
                              </Tooltip>
                              <Tooltip title={_(msg`Eliminar`)}>
                                <Button onClick={() => handleDeleteConfig(row)} color="inherit">
                                  <Delete sx={{ color: '#D4D8D9' }} />
                                </Button>
                              </Tooltip>
                              <Tooltip title={_(msg`Ver`)}>
                                <Button onClick={() => handleViewConfigDrawer(row)}>
                                  <SheetIcon color="#D4D8D9" />
                                </Button>
                              </Tooltip>
                            </>
                          ) : (
                            <Tooltip title={_(msg`Crear`)}>
                              <Button onClick={() => handleCreateConfig(row)}>
                                <AddCircle sx={{ color: '#D4D8D9' }} />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
          <StandardListPagination
            totalItems={totalCount}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={(page) => handlePageChange(page)}
            onRowsPerPageChange={(rowsPerPage) => {
              handleRowsPerPageChange(rowsPerPage);
            }}
            my={2}
          />
        </Box >
      )}
    </>
  );
};

export default DeviceConfigurationTable;
